import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import * as Care from 'src/replicant/care';
import { CareItem } from 'src/replicant/care/care.ruleset';
import StateObserver from 'src/StateObserver';
import sounds from 'src/lib/sounds';
import { CareActionType } from '../care/care.redux';
import * as Visit from 'src/replicant/visit';
import LockedButtonOverlay from './LockedButtonOverlay';
import { getFriendsPet, getPet } from '../pet';
import { getGrowthCapsuleDisabledReason } from 'src/replicant/care/care.getters';
import { getTutorialController } from '../tutorial/TutorialController';
import { puzzleCoinsConfirmCareItem } from '../puzzlecoins/puzzlecoins.sequence';

const ui = uiConfig.buruburu;

export type CareActionButtonType =
  | Exclude<CareActionType, 'evolve' | 'ascension'>
  | 'growthcapsule';

export type CareActionSelection = {
  type: CareActionButtonType;
  careItemId?: CareItem['id'];
  multiplier?: number;
};
type CareActionButtonOpts = {
  type: CareActionButtonType;
  onSelect: (selection: CareActionSelection) => Promise<void>;
  isLocked?: boolean;
};

export default class CareActionButton extends ButtonScaleViewWithText {
  private rulesetType: Care.ActionType | Care.ItemType | 'growthcapsule';

  constructor(private opts: CareActionButtonOpts) {
    super({
      ...ui.menu.menuButton,
      icon: ui.care.actionIcons[opts.type],
      localeText: () => i18n(`careAction.${opts.type}`),
      tracking: { feature: 'careMenu', $subFeature: opts.type },
    });
    this.rulesetType =
      this.opts.type === 'firstAid' ? 'first_aid' : this.opts.type;

    this.icon?.updateOpts({ scaleMethod: 'contain' });

    if (opts.isLocked) {
      const { width, height } = this.style;
      this.addSubview(new LockedButtonOverlay({ width, height }));
    }
  }

  public onClick = async () => {
    sounds.sfx.play('ok');

    if (this.opts.isLocked && this.opts.type === 'growthcapsule') {
      const { user } = StateObserver.getState();
      const now = StateObserver.now();
      await openPopupPromise('popupFeatureDisabled', {
        feature: 'growthcapsule',
        reason: getGrowthCapsuleDisabledReason(user, now),
        origin: 'hud',
      });
      return;
    }

    if (!this.checkIfCareActionAllowed()) {
      const careMenu = this.getSuperview()?.getSuperview(); // TODO decouple
      careMenu?.emit('ForceClose');
      await this.showCareActionBlockerConversation();
      return;
    }

    const { type } = this.opts;
    let careItemId: CareItem['id'] | undefined = undefined;
    switch (this.rulesetType) {
      case 'food':
      case 'snack':
      case 'toy':
      case 'first_aid':
        do {
          careItemId = await openPopupPromise('popupSelectCareItem', {
            type: this.rulesetType,
            origin: 'hud',
            additionalTracking: {
              itemType: this.rulesetType,
            },
          });

          if (careItemId) {
            let result: { accepted: boolean } | undefined;

            const { user } = StateObserver.getState();
            // If puzzle coins are enabled, confirm the care item purchase without a confirmation popup
            result = await puzzleCoinsConfirmCareItem(careItemId);

            if (result?.accepted) {
              await getTutorialController().handleEvent('careItemConfirmed');
              await this.opts.onSelect({ type, careItemId });
              break;
            }
          }
        } while (careItemId);

        await getTutorialController().handleEvent('carePopupClosed');
        break;

      case 'toilet':
      case 'bath':
        await this.opts.onSelect({ type });
        break;

      case 'growthcapsule':
        {
          await getTutorialController().handleEvent('growthCapsulePurchase');

          const confirm = await openPopupPromise('popupConfirmGrowthCapsule', {
            origin: 'hud',
          });

          if (confirm) {
            await this.opts.onSelect({ type });
          }
        }
        break;
    }
  };

  private checkIfCareActionAllowed() {
    const { user } = StateObserver.getState();
    const isVisiting = Visit.getters.isVisiting(user);
    const now = StateObserver.now();
    // On visiting allow only first_aid if friend pet is dying
    const isFriendDying = isVisiting && getFriendsPet()?.state === 'dying';
    // Make sure we're allowed to perform this care action
    const canPerformAction = Care.getters.canPerformCareAction(
      user,
      this.rulesetType,
      now,
    );
    const isCareActionsAllowed = !isFriendDying && canPerformAction;

    // Can perform
    if (isCareActionsAllowed) {
      return true;
    }

    // Allow first aid
    if (isFriendDying && this.rulesetType === 'first_aid') {
      return true;
    }

    return false;
  }

  private async showCareActionBlockerConversation() {
    const { user } = StateObserver.getState();
    const isVisiting = Visit.getters.isVisiting(user);
    // On visiting allow only first_aid if friend pet is dying
    const isFriendDying = isVisiting && getFriendsPet()?.state === 'dying';
    // What pet should talk
    const pet = isFriendDying ? getFriendsPet() : getPet();

    await pet?.startConversation('care_action_blocker');
  }
}
